// components/artifacts/EditHistoryModal.js - 수정 히스토리 모달

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * 수정 히스토리 모달 컴포넌트
 * @param {object} props
 * @param {boolean} props.open - 모달 열림 여부
 * @param {Array} props.history - 히스토리 배열 [{ id, prompt, originalText, modifiedMarkdown, from, to, status, createdAt }]
 * @param {function} props.onClose - 모달 닫기 콜백
 */
export default function EditHistoryModal({ open, history = [], loading = false, onClose }) {
  const [expandedId, setExpandedId] = React.useState(null);
  const [compareView, setCompareView] = React.useState({}); // { [id]: 'original' | 'modified' | 'split' }

  if (!open) return null;

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "방금 전";
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString("ko-KR");
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(17, 24, 39, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          width: "min(1240px, 98vw)",
          maxHeight: "85vh",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.3)",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* 모달 내부에서만 Markdown 기본 스타일 복원 */}
        <style>{`
          .edit-history-markdown {
            color: #111827;
            line-height: 1.65;
            font-size: 14px;
          }
          .edit-history-markdown p { margin: 0.5rem 0; }
          .edit-history-markdown h1 { font-size: 1.25rem; font-weight: 900; margin: 0.9rem 0 0.5rem; }
          .edit-history-markdown h2 { font-size: 1.15rem; font-weight: 900; margin: 0.85rem 0 0.45rem; }
          .edit-history-markdown h3 { font-size: 1.05rem; font-weight: 900; margin: 0.75rem 0 0.4rem; }
          .edit-history-markdown h4, 
          .edit-history-markdown h5, 
          .edit-history-markdown h6 { font-size: 0.98rem; font-weight: 900; margin: 0.7rem 0 0.35rem; }
          .edit-history-markdown ul { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
          .edit-history-markdown ol { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
          .edit-history-markdown li { margin: 0.25rem 0; }
          .edit-history-markdown blockquote {
            margin: 0.6rem 0;
            padding: 0.6rem 0.75rem;
            border-left: 3px solid rgba(58, 102, 255, 0.45);
            background: rgba(58, 102, 255, 0.06);
            border-radius: 10px;
          }
          .edit-history-markdown code {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            font-size: 0.92em;
            background: rgba(17, 24, 39, 0.06);
            padding: 0.15rem 0.3rem;
            border-radius: 6px;
          }
          .edit-history-markdown pre {
            background: rgba(17, 24, 39, 0.06);
            padding: 0.75rem;
            border-radius: 12px;
            overflow: auto;
          }
          .edit-history-markdown pre code {
            background: transparent;
            padding: 0;
          }
          .edit-history-markdown table {
            width: 100%;
            border-collapse: collapse;
            margin: 0.75rem 0;
          }
          .edit-history-markdown th,
          .edit-history-markdown td {
            border: 1px solid #e5e7eb;
            padding: 0.4rem 0.5rem;
            vertical-align: top;
          }
          .edit-history-markdown th {
            background: rgba(17, 24, 39, 0.04);
            font-weight: 800;
          }
          .edit-history-markdown a { color: #1d4ed8; text-decoration: underline; }
        `}</style>

        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: "18px", color: "#111827" }}>수정 히스토리</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "24px",
              cursor: "pointer",
              color: "#6b7280",
              padding: "4px 8px",
            }}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {/* 히스토리 리스트 */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
              불러오는 중...
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
              수정 히스토리가 없습니다.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {history.map((item) => {
                const isExpanded = expandedId === item.id;
                const view = compareView[item.id] || "modified";

                return (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    {/* 헤더 */}
                    <div
                      style={{
                        padding: "12px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, color: "#111827", marginBottom: "4px" }}>
                          {item.prompt || "수정 요청"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          {formatTime(item.createdAt)} · {item.status === "applied" ? "적용됨" : "되돌림됨"}
                        </div>
                      </div>
                    </div>

                    {/* 확장된 내용 */}
                    {isExpanded && (
                      <div style={{ padding: "16px", borderTop: "1px solid #e5e7eb", background: "#f9fafb" }}>
                        {/* 뷰 전환 버튼 */}
                        <div style={{ display: "flex", gap: "6px", marginBottom: "12px", background: "rgba(58, 102, 255, 0.1)", borderRadius: "8px", padding: "4px" }}>
                          <button
                            type="button"
                            onClick={() => setCompareView({ ...compareView, [item.id]: "original" })}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "none",
                              background: view === "original" ? "#3A66FF" : "transparent",
                              color: view === "original" ? "#fff" : "#111827",
                              fontWeight: 700,
                              fontSize: "13px",
                              cursor: "pointer",
                            }}
                          >
                            적용 전
                          </button>
                          <button
                            type="button"
                            onClick={() => setCompareView({ ...compareView, [item.id]: "modified" })}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "none",
                              background: view === "modified" ? "#3A66FF" : "transparent",
                              color: view === "modified" ? "#fff" : "#111827",
                              fontWeight: 700,
                              fontSize: "13px",
                              cursor: "pointer",
                            }}
                          >
                            적용 후
                          </button>
                          <button
                            type="button"
                            onClick={() => setCompareView({ ...compareView, [item.id]: "split" })}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "none",
                              background: view === "split" ? "#3A66FF" : "transparent",
                              color: view === "split" ? "#fff" : "#111827",
                              fontWeight: 700,
                              fontSize: "13px",
                              cursor: "pointer",
                            }}
                          >
                            비교
                          </button>
                        </div>

                        {/* 비교 콘텐츠 */}
                        <div
                          style={{
                            display: view === "split" ? "grid" : "block",
                            gridTemplateColumns: view === "split" ? "1fr 1fr" : "1fr",
                            gap: view === "split" ? "12px" : "0",
                            maxHeight: "400px",
                            overflowY: "auto",
                          }}
                        >
                          {/* 적용 전 */}
                          {(view === "original" || view === "split") && (
                            <div
                              style={{
                                background: view === "split" ? "rgba(239, 68, 68, 0.05)" : "rgba(255,255,255,0.85)",
                                border: view === "split" ? "1px solid rgba(239, 68, 68, 0.2)" : "1px solid #e5e7eb",
                                borderRadius: "8px",
                                padding: "12px",
                              }}
                            >
                              {view === "split" && (
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "#dc2626", marginBottom: "8px", textTransform: "uppercase" }}>
                                  적용 전
                                </div>
                              )}
                              <div className="edit-history-markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {item.originalText || ""}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}

                          {/* 적용 후 */}
                          {(view === "modified" || view === "split") && (
                            <div
                              style={{
                                background: view === "split" ? "rgba(16, 185, 129, 0.05)" : "rgba(255,255,255,0.85)",
                                border: view === "split" ? "1px solid rgba(16, 185, 129, 0.2)" : "1px solid #e5e7eb",
                                borderRadius: "8px",
                                padding: "12px",
                              }}
                            >
                              {view === "split" && (
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "#10b981", marginBottom: "8px", textTransform: "uppercase" }}>
                                  적용 후
                                </div>
                              )}
                              <div className="edit-history-markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {item.modifiedMarkdown || ""}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
